import { useMemo, useState } from "react";
import { MaterialReactTable } from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { useCuentaCorrienteSoloPendientes } from "../../envasados/clientesEnvasado/cuentaCorrienteEnvasado/hooks/useCuentaCorrienteSoloPendientes";
import { buildColumnsCuentaCorrienteEnvasado } from "../../envasados/clientesEnvasado/cuentaCorrienteEnvasado/config/columns";
import { TABLE_VARIANTS } from "../../envasados/clientesEnvasado/cuentaCorrienteEnvasado/config/TABLE_VARIANTS";

const CtaCtePendienteModal = ({ open, onClose, cliente }) => {
  const { data, loading, refresh } = useCuentaCorrienteSoloPendientes(cliente);
  const [copiedId, setCopiedId] = useState(null);

  const columns = useMemo(
    () =>
      buildColumnsCuentaCorrienteEnvasado({
        variant: TABLE_VARIANTS.PENDIENTE,
        totales: data?.saldos,
        copiedId,
        setCopiedId,
      }),
    [data, copiedId],
  );

  const estaVencidaConSaldoPendiente = (
    comprobante,
    fechaVencimiento,
    saldoPendiente,
  ) => {
    if (String(comprobante).toLocaleLowerCase() !== "factura") return false;
    return (
      saldoPendiente > 0 && new Date(fechaVencimiento).getTime() < Date.now()
    );
  };

  const dataConEstado = useMemo(() => {
    return data?.movimientos.map((row) => ({
      ...row,
      _vencidaConSaldoPendiente: estaVencidaConSaldoPendiente(
        row.comprobante,
        row.fecha_vencimiento,
        row.saldoImputado ?? row.pendiente,
      ),
    }));
  }, [data]);

  const bgColorTableHeadCell = "rgba(212, 230, 241, 0.8)";

  const tableHeadCellProps = {
    align: "left",
    sx: {
      fontSize: 14,
      color: "black",
      fontWeight: "normal",
      paddingTop: 1,
      paddingBottom: 1,
      backgroundColor: bgColorTableHeadCell,
      borderColor: "#FDFEFE",
    },
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle
        sx={{
          bgcolor: "primary.main",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <ReceiptLongIcon sx={{ fontSize: 30 }} />

          <Box>
            <Typography variant="h6" fontWeight={600}>
              Cuenta Corriente Pendiente
            </Typography>

            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {cliente?.id} - {cliente?.razon_social}
            </Typography>
          </Box>
        </Box>

        <IconButton onClick={onClose} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          bgcolor: "#fafafa",
          p: 3,
        }}
      >
        <Box display="flex" gap={2} my={3}>
          <Paper
            elevation={1}
            sx={{
              p: 2,
              flex: 1,
              borderRadius: 2,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Total Pendiente
            </Typography>

            <Typography variant="h5" fontWeight={700}>
              $ {cliente.total_cta_cte}
            </Typography>
          </Paper>

          <Paper
            elevation={1}
            sx={{
              p: 2,
              flex: 1,
              borderRadius: 2,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Comprobantes
            </Typography>

            <Typography variant="h5" fontWeight={700}>
              0
            </Typography>
          </Paper>

          <Paper
            elevation={1}
            sx={{
              p: 2,
              flex: 1,
              borderRadius: 2,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Mayor atraso
            </Typography>

            <Typography variant="h5" fontWeight={700}>
              {cliente.dias_mayor_atraso} días
            </Typography>
          </Paper>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <MaterialReactTable
          localization={MRT_Localization_ES}
          columns={columns}
          data={dataConEstado ?? []}
          enableSorting={false}
          enableColumnFilterModes={false}
          enableColumnOrdering={false}
          enableGrouping={false}
          enableColumnActions={false}
          enableColumnFilters={false}
          enableDensityToggle={false}
          enableRowSelection={false}
          enableHiding={false}
          enableStickyHeader
          enableStickyFooter
          initialState={{
            density: "compact",
            pagination: {
              pageIndex: 0,
              pageSize: 100,
            },
          }}
          state={{
            isLoading: loading,
            showSkeletons: loading,
            showLoadingOverlay: loading,
          }}
          muiTableContainerProps={{
            sx: {
              height: "calc(100vh - 220px)",
            },
          }}
          muiTableHeadCellProps={tableHeadCellProps}
          muiTableFooterCellProps={{
            sx: {
              backgroundColor: bgColorTableHeadCell,
              borderColor: "#FDFEFE",
            },
          }}
          muiTableBodyCellProps={({ row }) => ({
            align: "left",
            sx: {
              backgroundColor: row.original._vencidaConSaldoPendiente
                ? row.index % 2 === 0
                  ? "#FEF3C7"
                  : "#ffeea8"
                : row.index % 2 === 0
                  ? "#ffffff"
                  : "#fafafa",

              color: "black",
              borderBottom: "1px solid rgba(224,224,224,1)",

              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            },
            hover: true,
          })}
          muiTableBodyRowProps={({ row }) => {
            const { debe, haber } = row.original;

            let color = "inherit";

            if (debe) color = "success.main";
            if (haber) color = "error.main";

            return {
              sx: {
                "& td": {
                  color,
                  fontWeight: 600,
                },
              },
            };
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default CtaCtePendienteModal;
